/*
 *   GTMEnhancedEcommerce
 *   This script will make it possible to fire google enhanced ecommerce events
 *   Documentation can be found https://developers.google.com/tag-manager/enhanced-ecommerce
 *
 */
/* global dataLayer */
/*
 *   This script is used to fire the enhanced ecommerce events to google tag manager.
 *   It is dependend of a set of data-attributes which may differ per event.
 */

import formatPhoneNumber from "helpers/format-phonenumber/formatPhonenumber";

export const GtmEnhancedEcommerce = (function () {
  const setEventListeners = () => {
    // Handle click events
    window.addEventListener("click", (e) => {
      handleProductClicks(e);
      handleRemoveFromCart(e);
      handleCheckoutOption(e);
      /* TODO: Handle promotion clicks */
    });
    // Handle load events
    window.addEventListener("load", () => {
      handleSubCategory();
      handleProductDetail();
      handleAddToCart();
      handleCheckout();
      handlePurchase();
      handleB2BCustomer();
      /* TODO: handle promotion impression */
    });
  };

  /*-----------------------------------------------------
    PUSH TO DATALAYER. Helper function to push collected data to the datalayer.
    ------------------------------------------------------ */
  const pushToDataLayer = (data) => {
    if (data && dataLayer) {
      dataLayer.push(data);
    }
  };

  /*-----------------------------------------------------
    BUILD PRODUCT ARRAY. Helper function to build an array with products to send to google analytics
    ------------------------------------------------------ */
  const buildProductArray = (productElements, keys, list = null) => {
    const products = []; //create an empty array so we can store the products

    //Check if there are product elements and the keys are set.
    if (productElements && productElements.length && keys) {
      productElements.forEach((productEl) => {
        const collectedProducts = {}; //Create product object to collect all the product data
        if (productEl) {
          if (
            productEl.hasAttribute("data-ec-set-product") &&
            productEl.getAttribute("data-ec-set-product") === "true"
          ) {
            return;
          }

          //Loop over all the keys to create the object
          keys.forEach((key) => {
            const dataLabel = "data-ec-" + key; //Create label to get the data attribute
            //Check if data-attribute exist at element
            if (productEl.hasAttribute(dataLabel)) {
              let value = productEl.getAttribute(dataLabel); //Get the value for data attribute

              //Convert to int or float for certain properties
              if (key === "quantity" || key === "position") {
                value = parseInt(value);
              } else if (key === "price") {
                value = parseFloat(value);
              }

              collectedProducts[key] = value; //Set key value in productObj
            }
          });

          // override list from product if list is given as function param
          if (list) {
            collectedProducts["list"] = list;
          }

          //Check if name or id are set. These are required field for Enhanced Ecommerce
          if (collectedProducts.name || collectedProducts.id) {
            products.push(collectedProducts); //Add product to products array
          }
        }
      });
    }
    return products; //Return products
  };

  /*-----------------------------------------------------
    HANDLE PRODUCT CLICK. This function registers product clicks and fires an ec event.
    It is split up in 2 functions because it's needed to call the handleProductClick from the javascript
    ------------------------------------------------------ */
  const handleProductClicks = (e) => {
    // handle all product click elements
    const element = e.target.closest("[data-ec-productclick]");

    if (element) {
      handleProductClick(element);
    }
  };

  const handleProductClick = (element) => {
    const data = {
      event: "productClick",
      ecommerce: {
        click: {
          actionField: { list: element.getAttribute("data-list") },
          products: [
            {
              category: element.getAttribute("data-ec-category"),
              name: element.getAttribute("data-ec-name"),
              id: element.getAttribute("data-ec-id"),
              price: element.getAttribute("data-ec-price"),
              brand: element.getAttribute("data-ec-brand"),
              position: element.getAttribute("data-ec-position"),
            },
          ],
        },
      },
    };

    pushToDataLayer(data);
  };

  /*-----------------------------------------------------
      HANDLE REMOVE FROM CART. This function finds all removefromcart elements and fires an ec event
    ------------------------------------------------------ */
  const handleRemoveFromCart = (e) => {
    const cartElement = e.target.closest("[data-ec-removefromcart]");

    if (cartElement) {
      const data = {
        event: "removeFromCart",
        ecommerce: {
          remove: {
            products: [
              {
                category: cartElement.getAttribute("data-ec-category"),
                name: cartElement.getAttribute("data-ec-name"),
                id: cartElement.getAttribute("data-ec-id"),
                price: cartElement.getAttribute("data-ec-price"),
                brand: cartElement.getAttribute("data-ec-brand"),
                quantity: cartElement.getAttribute("data-ec-quantity"),
              },
            ],
          },
        },
      };

      pushToDataLayer(data);
    }
  };

  /*-----------------------------------------------------
          HANDLE SUBCATEGORY.
    ------------------------------------------------------ */

  const handleSubCategory = () => {
    const subCategoryElement = document.querySelector(
      "[data-ec-subcategoryimpression]",
    );

    if (subCategoryElement) {
      const products = subCategoryElement.querySelectorAll("[data-ec-name]");
      const data = {
        ecommerce: {
          subcategoryImpressions: {
            products: buildProductArray(
              products,
              [
                "position",
                "category",
                "name",
                "id",
                "price",
                "brand",
                "quantity",
              ],
              "subcategory",
            ),
          },
        },
      };
      pushToDataLayer(data);
    }
  };

  /*-----------------------------------------------------
          HANDLE PRODUCT DETAIL.
    ------------------------------------------------------ */
  const handleProductDetail = () => {
    const productDetailImpressionElement = document.querySelector(
      "[data-ec-productdetailimpression]",
    );

    if (productDetailImpressionElement) {
      const data = {
        event: "productDetails",
        ecommerce: {
          detail: {
            products: [
              {
                category:
                  productDetailImpressionElement.getAttribute(
                    "data-ec-category",
                  ),
                name: productDetailImpressionElement.getAttribute(
                  "data-ec-name",
                ),
                id: productDetailImpressionElement.getAttribute("data-ec-id"),
                price:
                  productDetailImpressionElement.getAttribute("data-ec-price"),
                brand:
                  productDetailImpressionElement.getAttribute("data-ec-brand"),
              },
            ],
          },
        },
      };

      pushToDataLayer(data);
    }
  };

  /*-----------------------------------------------------
          HANDLE ADD TO CART. It is split up in 2 functions because it's needed to call the handleAddToCart from the javascript
    ------------------------------------------------------ */
  const handleAddToCart = () => {
    const addToCartImpressionElement = document.querySelector(
      "[data-ec-addtocart]",
    );
    if (!addToCartImpressionElement) {
      return;
    }
    const products =
      addToCartImpressionElement.querySelectorAll("[data-ec-name]");
    const productData = buildProductArray(products, [
      "name",
      "id",
      "price",
      "brand",
      "quantity",
      "category",
    ]);

    if (productData.length) {
      fireAddToCart(productData);
    }
  };

  const fireAddToCart = (products) => {
    const data = {
      event: "addToCart",
      ecommerce: {
        currencyCode: "EUR",
        add: {
          products: products,
        },
      },
    };

    pushToDataLayer(data);
  };

  /*-----------------------------------------------------
          CHECKOUT. Handle checkout measurements
    ------------------------------------------------------ */
  const handleCheckout = () => {
    const checkoutPage = document.querySelector("[data-ec-checkout]");
    //Check if this is a checkout step
    if (checkoutPage) {
      const checkoutStep = parseInt(
        checkoutPage.getAttribute("data-ec-checkout-step"),
      ); //Get the checkout step
      const checkoutProducts =
        checkoutPage.querySelectorAll("[data-ec-product]"); //Get all the product elements
      //Collect all the data and create object to send to datalayer.
      const data = {
        event: "checkout",
        ecommerce: {
          checkout: {
            actionField: { step: checkoutStep },
            products: buildProductArray(checkoutProducts, [
              "name",
              "id",
              "price",
              "brand",
              "quantity",
              "category",
            ]),
          },
        },
      };

      pushToDataLayer(data); //Send data to datalayer
    }
  };

  /*-----------------------------------------------------
      CHECKOUT OPTION. Handle checkout option measurements on click
    ------------------------------------------------------ */
  //Create options object to so we can store the option information when switched
  const optionsObj = {
    step: null,
    option: null,
  };
  const handleCheckoutOption = (event) => {
    const submitStepBtn = event.target.closest(
      "[data-ec-checkout-option-submit]",
    ); //Check if clicked button is the submit button
    const optionElement = event.target.closest("[data-ec-checkout-option]"); //Check if the clicked element is an option switch

    //If closest object is option element store date in the option object
    if (optionElement) {
      optionsObj.option = optionElement.getAttribute("data-ec-checkout-option"); //Get the option name
    }

    //Only send the data when the submit button is clicked
    if (submitStepBtn) {
      const checkoutPage = document.querySelector("[data-ec-checkout]"); //Get the checkout page element
      //Check if this is a checkout step
      if (checkoutPage) {
        optionsObj.step = parseInt(
          checkoutPage.getAttribute("data-ec-checkout-step"),
        ); //Get action step

        //If optionsObj.option is empty try to get selected option
        if (!optionsObj.option) {
          const checkedOption = checkoutPage.querySelector(
            "[data-ec-checkout-option]:checked",
          );
          optionsObj.option = checkedOption
            ? checkedOption.getAttribute("data-ec-checkout-option")
            : null;
        }

        //Collect all the data and create object to send to datalayer.
        if (optionsObj.step && optionsObj.option) {
          const data = {
            event: "checkoutOption",
            ecommerce: {
              // eslint-disable-next-line camelcase
              checkout_option: {
                actionField: {
                  step: optionsObj.step,
                  option: optionsObj.option,
                },
              },
            },
          };

          pushToDataLayer(data); //Send data to datalayer
        }
      }
    }
  };

  /*-----------------------------------------------------
      PURCHASE. Handle purchase measurements
    ------------------------------------------------------ */
  const handlePurchase = () => {
    const purchasePage = document.querySelector("[data-ec-purchase]");

    //Check if this is the purchase page
    if (purchasePage) {
      const isFinalize = purchasePage.hasAttribute("data-ec-finalized");
      const checkoutProducts =
        purchasePage.querySelectorAll("[data-ec-product]");

      //Collect all the data and create object to send to datalayer.
      if (isFinalize) {
        const data = {
          event: "purchase",
          ecommerce: {
            purchase: {
              actionField: {
                id: purchasePage.getAttribute("data-ec-id"),
                affiliation: purchasePage.getAttribute("data-ec-affiliation"),
                revenue: purchasePage.getAttribute("data-ec-revenue"),
                tax: purchasePage.getAttribute("data-ec-tax"),
                shipping: purchasePage.getAttribute("data-ec-shipping"),
                coupon: purchasePage.getAttribute("data-ec-coupon"),
                email: purchasePage.getAttribute("data-ec-customer-email"),
                // format phone number to add country prefix (otherwise Google can't read it)
                // eslint-disable-next-line camelcase
                phone_number: formatPhoneNumber(
                  purchasePage.getAttribute("data-ec-customer-phone"),
                  purchasePage.getAttribute("data-ec-customer-country-code"),
                ),
                // eslint-disable-next-line camelcase
                home_address: {
                  street: purchasePage.getAttribute("data-ec-customer-street"),
                  city: purchasePage.getAttribute("data-ec-customer-city"),
                  // eslint-disable-next-line camelcase
                  postal_code: purchasePage.getAttribute(
                    "data-ec-customer-postal-code",
                  ),
                  country: purchasePage.getAttribute(
                    "data-ec-customer-country",
                  ),
                },
              },
              products: buildProductArray(checkoutProducts, [
                "name",
                "id",
                "price",
                "mpn",
                "brand",
                "quantity",
                "category",
              ]),
            },
          },
        };
        pushToDataLayer(data); //Send data to datalayer
      }

      //Send channel to data layer, so a deduplication meganism can be created in GTM
      let channel = purchasePage.getAttribute("data-gtm-channel");
      if (channel) {
        channel = channel !== "online" ? "other" : "online";
        pushToDataLayer({
          channel: channel,
        });
      }
    }
  };

  /*-----------------------------------------------------
      B2B. Handle B2B customer
    ------------------------------------------------------ */
  const handleB2BCustomer = () => {
    // set true if B2B customer is logged in (based on attribute on <body>)
    const isB2BCustomer = document
      .querySelector("body")
      .hasAttribute("data-b2b-customer");
    // fill data for datalayer
    const data = {
      customerIsB2B: isB2BCustomer,
    };
    pushToDataLayer(data);
  };

  return {
    init: setEventListeners,
    handleProductClick: handleProductClick,
    fireAddToCart: fireAddToCart,
  };
})();
